import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaPaperPlane, FaTimes } from 'react-icons/fa';
import './EmailModal.css';

/**
 * Reusable email dialog.
 *
 * Props:
 * - open: boolean (controls visibility)
 * - title: string (modal title)
 * - initialTo: string
 * - initialCc: string
 * - initialBcc: string
 * - initialSubject: string
 * - initialMessage: string
 * - allowCcBcc: boolean (default: false)
 * - onClose: () => void  (called when user cancels/closes)
 * - onSend: ({to, cc, bcc, subject, message, attachmentsSelected}) => Promise<void> | void
 * - confirmText: string (default: "Send Email")
 * - cancelText: string (default: "Cancel")
 * - extraContent: ReactNode (optional custom UI rendered under Message)
 */
export default function EmailModal({
  open,
  title = 'Email',
  initialTo = '',
  initialCc = '',
  initialBcc = '',
  initialSubject = '',
  initialMessage = '',
  allowCcBcc = false,
  // Optional: list of attachments to display for selection
  // Shape: [{ filename: string, date?: string|number|Date, description?: string }]
  attachments = [],
  onClose,
  onSend,
  confirmText = 'Send Email',
  cancelText = 'Cancel',
  extraContent = null,
  showAttachmentsToggle = false,
  attachmentsToggleLabel = 'Show attachments',
  attachmentsToggleChecked = false,
  onAttachmentsToggle = null,
  attachmentsLoading = false,
}) {
  const [to, setTo] = useState(initialTo);
  const [cc, setCc] = useState(initialCc);
  const [bcc, setBcc] = useState(initialBcc);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState(initialMessage);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const firstInputRef = useRef(null);
  const [attachSel, setAttachSel] = useState(new Set());

  // Normalize a recipient list: split on commas/semicolons, trim, and join with commas
  const canonicalizeList = (value) => {
    const parts = String(value || '')
      .split(/[;,]/)
      .map(s => s.trim())
      .filter(Boolean);
    return parts.join(', ');
  };

  // sync fields when opened or initial props change
  useEffect(() => {
    if (open) {
      // Replace any semicolons with commas and normalize spacing
      setTo(canonicalizeList(initialTo));
      setCc(canonicalizeList(initialCc));
      setBcc(canonicalizeList(initialBcc));
      setSubject(initialSubject || '');
      setMessage(initialMessage || '');
      setError('');
      // default-select all attachments when dialog opens
      try {
        const names = Array.isArray(attachments)
          ? attachments.map(a => String(a?.filename || '').split('/').pop()).filter(Boolean)
          : [];
        setAttachSel(new Set(names));
      } catch { setAttachSel(new Set()); }
      // focus the "to" field shortly after mount
      setTimeout(() => { try { firstInputRef.current?.focus(); } catch {} }, 0);
    }
  }, [open, initialTo, initialCc, initialBcc, initialSubject, initialMessage, attachments]);

  // close on ESC
  useEffect(() => {
    const handler = (e) => { if (open && e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const validateEmails = (value) => {
    if (!value) return true;
    // allow comma or semicolon separated list
    const parts = String(value).split(/[;,]/).map(s => s.trim()).filter(Boolean);
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return parts.every(p => re.test(p));
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('eml-modal__overlay')) onClose?.();
  };

  const handleSend = useCallback(async () => {
    setError('');
    if (!to || !validateEmails(to)) {
      setError('Please enter a valid recipient email (you can separate multiple with commas).');
      return;
    }
    if (cc && !validateEmails(cc)) {
      setError('One or more CC emails look invalid.');
      return;
    }
    if (bcc && !validateEmails(bcc)) {
      setError('One or more BCC emails look invalid.');
      return;
    }
    setSending(true);
    try {
      // Ensure we send canonicalized lists
      await onSend?.({
        to: canonicalizeList(to),
        cc: canonicalizeList(cc),
        bcc: canonicalizeList(bcc),
        subject,
        message,
        // Provide selected attachment filenames (basename) to caller
        attachmentsSelected: Array.from(attachSel || new Set()),
      });
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  }, [to, cc, bcc, subject, message, onSend, onClose]);

  if (!open) return null;

  return (
    <div
      className="eml-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
      onClick={handleOverlayClick}
    >
      <div className="eml-modal">
        <div className="eml-modal__header">
          <h2 id="email-modal-title">{title}</h2>
          <button
            className="eml-modal__close"
            onClick={() => onClose?.()}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="eml-modal__body">
          {error ? <div className="eml-modal__error" role="alert">{error}</div> : null}

          <div className="eml-form">
            <label>
              <span>To</span>
              <input
                ref={firstInputRef}
                type="text"
                placeholder="to@example.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>

            {allowCcBcc && (
              <>
                <label>
                  <span>CC</span>
                  <input
                    type="text"
                    placeholder="cc@example.com"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                  />
                </label>
                <label>
                  <span>BCC</span>
                  <input
                    type="text"
                    placeholder="bcc@example.com"
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                  />
                </label>
              </>
            )}

            <label>
              <span>Subject</span>
              <input
                type="text"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>

          <label>
            <span>Message</span>
            <textarea
              placeholder="Write a message…"
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>

          {extraContent}

            {(showAttachmentsToggle || (Array.isArray(attachments) && attachments.length > 0)) && (
              <div className="eml-attach-block">
                <div className="eml-attach-head">
                  <strong>Attachments</strong>
                  <div className="eml-attach-actions">
                    {showAttachmentsToggle && (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                        <input
                          type="checkbox"
                          checked={!!attachmentsToggleChecked}
                          onChange={(e) => onAttachmentsToggle?.(e.target.checked)}
                        />
                        {attachmentsToggleLabel}
                      </label>
                    )}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setAttachSel(new Set(attachments.map(a => String(a?.filename || '').split('/').pop()).filter(Boolean)))}
                      disabled={!!showAttachmentsToggle && (!attachmentsToggleChecked || attachmentsLoading || !attachments.length)}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setAttachSel(new Set())}
                      disabled={!!showAttachmentsToggle && (!attachmentsToggleChecked || attachmentsLoading || !attachments.length)}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                {showAttachmentsToggle && !attachmentsToggleChecked ? (
                  <div className="eml-attach-foot" style={{ textAlign: 'left' }}>
                    Attachments are hidden by default. Check the box to load them if you need to review or include additional files.
                  </div>
                ) : attachmentsLoading ? (
                  <div className="eml-attach-foot" style={{ textAlign: 'left' }}>
                    Loading attachments…
                  </div>
                ) : (Array.isArray(attachments) && attachments.length > 0) ? (
                  <>
                    <div className="eml-attach-tablewrap">
                      <table className="eml-attach-table">
                        <thead>
                          <tr>
                            <th style={{ width: 42 }}>Send</th>
                            <th>Filename</th>
                            <th style={{ width: 180 }}>Date</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attachments.map((a, idx) => {
                            const full = String(a?.filename || '');
                            const name = full.split('/').pop();
                            const checked = attachSel.has(name);
                            const dt = a?.date ? new Date(a.date) : null;
                            const dateText = dt && !isNaN(dt.getTime()) ? dt.toLocaleString() : '';
                            return (
                              <tr key={name + idx}>
                                <td>
                                  <input type="checkbox" checked={checked} onChange={(e) => {
                                    setAttachSel(prev => { const n = new Set(prev); if (e.target.checked) n.add(name); else n.delete(name); return n; });
                                  }} />
                                </td>
                                <td title={full}>{name}</td>
                                <td>{dateText}</td>
                                <td className="nowrap">{a?.description || ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="eml-attach-foot">Invoice PDF is always attached. Select any additional files to include.</div>
                  </>
                ) : (
                  <div className="eml-attach-foot" style={{ textAlign: 'left' }}>No attachments found for this invoice.</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="eml-modal__footer">
          <button className="btn" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : (<><FaPaperPlane style={{ marginRight: 6 }} />{confirmText}</>)}
          </button>
          <button className="btn btn-secondary" onClick={() => onClose?.()} disabled={sending}>
            <FaTimes style={{ marginRight: 6 }} />{cancelText}
          </button>
        </div>
      </div>
    </div>
  );
}
